import escape from 'lodash/escape';
import isEqual from 'lodash/isEqual';
import { makeObservable, override } from 'mobx';

import type { DiagnosisParams } from 'chaika-frontend-types/frontend-cms';
import { DictionaryNames, MedicalCaseStatus } from 'chaika-enums';
import type { Optional } from 'chaika-common-types';

import { store } from '@store';
import { DiagnosisList } from '@store/models/SystemFields/Diagnosis';
import { AbstractField } from './AbstractField';
import { translate } from '@chaika/i18n/helper';

export class DiagnosisField extends AbstractField<DiagnosisList, DiagnosisParams[]> {
    constructor(params: DiagnosisParams[], name: string) {
        super(params, name);
        makeObservable(this);
    }

    public get hasHtmlPreview(): boolean {
        return true;
    }

    @override
    public get hasData(): boolean {
        return this.fieldValue.getSize() > 0;
    }

    public isEqual(value: DiagnosisList): boolean {
        const diagnoses = this.getUpdateParams();

        return isEqual(diagnoses, value.serialize());
    }

    public hasItem(id: string): boolean {
        return this.fieldValue.has(id);
    }

    public getItem(id: string): Optional<DiagnosisParams> {
        return this.fieldValue.get(id);
    }

    public makeUpdateParams(): DiagnosisParams[] {
        return this.fieldValue.serialize();
    }

    public getPreview(): string {
        const serializedDiagnoses = this.fieldValue.serialize();

        return serializedDiagnoses.map(diagnosis => this.getDiagnosisRevision(diagnosis)).join('\n');
    }

    public getItemRevision(id: string): string {
        const diagnosis = this.fieldValue.getExisting(id);
        const prefix = diagnosis.status === MedicalCaseStatus.Closed ? `${translate('closed')} ` : '';

        return `${prefix}${diagnosis.clinicalDiagnosis}`;
    }

    public getItemTitle(id: string): string {
        const diagnosis = this.fieldValue.get(id);
        const { dictionary } = store;

        const icdDictionary = dictionary.getDictionary(DictionaryNames.Mkb10);
        const icd = diagnosis && icdDictionary.get(diagnosis.icdId);

        return icd?.value ?? '';
    }

    public getItemContent(id: string): string {
        const diagnosis = this.fieldValue.get(id);
        const { dictionary } = store;

        const icdDictionary = dictionary.getDictionary(DictionaryNames.Mkb10);
        const icd = diagnosis && icdDictionary.get(diagnosis.icdId);

        return icd?.title ?? '';
    }

    public getStatus(id: string): Optional<string> {
        const diagnosis = this.fieldValue.get(id);

        return diagnosis?.status === MedicalCaseStatus.Closed ? `${translate('closed')} ` : undefined;
    }

    public getDiagnosisRevision(diagnosis: DiagnosisParams): string {
        const { dictionary } = store;

        const icdDictionary = dictionary.getDictionary(DictionaryNames.Mkb10);
        const icd = icdDictionary.get(diagnosis?.icdId);
        const icdTitle = icd?.value ?? '';

        return `<strong>${escape(icdTitle)}</strong> ${escape(diagnosis.clinicalDiagnosis ?? '')}`;
    }

    public clone(value: DiagnosisParams[] = this.fieldValue.serialize()): DiagnosisField {
        return new DiagnosisField(value, this.fieldName);
    }

    protected setDefaultValueValue(): void {
        this.defaultNullValue = [];
    }

    protected setFieldValue(value: DiagnosisParams[]): void {
        this.fieldValue = new DiagnosisList(value);
    }
}
